import {Component, computed, effect, inject, input, output, ChangeDetectionStrategy} from "@angular/core"
import {MatTooltipModule} from "@angular/material/tooltip"
import {cardAnimations} from "@app/common/components/cards/animations"
import {BaseSelectComponent} from "@app/platform/components/base/base-select/base-select.component"
import {TemplateAddCardComponent} from "@app/template-editor/components/template-add-card/template-add-card.component"
import {CardErrorComponent} from "@common/components/cards/card-error/card-error.component"
import {CardPlaceholderComponent} from "@common/components/cards/card-placeholder/card-placeholder.component"
import {InfiniteListComponent} from "@common/components/lists/infinite-list/infinite-list.component"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {AuthService} from "@common/services/auth/auth.service"
import {
    GetMaterialRangeTagsListGQL,
    MaterialRangeTagItemFragment,
} from "@app/template-editor/components/template-add-other-material-range/template-add-other-material-range.generated"
import {TagFilterInput, TagType} from "@generated"
import {ConfigGroup, ConfigVariant, MaterialLikes, MaterialReference, MaterialSwitch, TemplateNodes} from "@cm/template-nodes"
import {v4 as uuid4} from "uuid"
import {LoadedDataItem} from "@app/platform/models/data"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-add-other-material-range",
    templateUrl: "./template-add-other-material-range.component.html",
    styleUrl: "./template-add-other-material-range.component.scss",
    animations: [...cardAnimations],
    imports: [InfiniteListComponent, CardErrorComponent, TemplateAddCardComponent, CardPlaceholderComponent, MatTooltipModule],
})
export class TemplateAddOtherMaterialRange extends BaseSelectComponent<MaterialRangeTagItemFragment, TagFilterInput> {
    readonly $search = input.required<string>({alias: "search"})
    private readonly tagsGql = inject(GetMaterialRangeTagsListGQL)

    private rawData = this.implementDataLoader(fetchThrowingErrors(this.tagsGql))

    readonly $data = computed(() => {
        const getDataWithGalleryImage = (
            item: LoadedDataItem<MaterialRangeTagItemFragment>,
        ): LoadedDataItem<MaterialRangeTagItemFragment & {galleryImage: {id: string} | null}> => {
            if (!item.data) return item

            const {data, ...rest} = item

            type AssignmentsWithGalleryImage = {
                __typename: "TagAssignment"
                assignedTo: {
                    __typename: "Material"
                    legacyId: number
                    galleryImage: {
                        __typename: "DataObject"
                        id: string
                    }
                }
            }

            const galleryImageId = data.assignments
                .filter(
                    (assignment): assignment is AssignmentsWithGalleryImage =>
                        assignment.assignedTo?.__typename === "Material" && assignment.assignedTo.galleryImage?.id !== undefined,
                )
                .at(0)?.assignedTo.galleryImage.id

            return {
                ...rest,
                data: {
                    ...data,
                    galleryImage: galleryImageId
                        ? {
                              id: galleryImageId,
                          }
                        : null,
                },
            }
        }

        return this.rawData().items.map((item) => ({
            ...getDataWithGalleryImage(item),
            node: item.data ? this.getConfigGroup(item.data) : undefined,
        }))
    })

    readonly onItemClicked = output()

    private readonly auth = inject(AuthService)

    constructor() {
        super()
        this.$customFilters.set({tagType: [TagType.MaterialRange]})

        effect(() => {
            this.$lockOrganization.set(!this.auth.$actingAsStaff())
        })

        effect(() => {
            const search = this.$search()
            this.searchText$.next(search)
        })
    }

    private getConfigGroup(data: MaterialRangeTagItemFragment) {
        const materialSwitch = new MaterialSwitch({parameters: {name: data.name, nodes: new MaterialLikes({parameters: {list: []}})}})
        const configGroup = new ConfigGroup({
            parameters: {
                name: data.name,
                nodes: new TemplateNodes({parameters: {list: []}}),
                id: uuid4(),
                displayWithLabels: true,
            },
        })

        for (const assignment of data.assignments) {
            if (assignment.assignedTo?.__typename !== "Material") continue
            const {assignedTo: material} = assignment
            const {latestCyclesRevision} = material
            if (!latestCyclesRevision) continue
            const materialName = material.name || `Material #${material.legacyId}`

            const materialReference = new MaterialReference({parameters: {name: materialName, materialId: material.legacyId, materialRevisionId: null}})
            materialSwitch.parameters.nodes.addEntry(materialReference)

            const configVariant = new ConfigVariant({
                parameters: {name: materialName, nodes: new TemplateNodes({parameters: {list: [materialReference]}}), id: uuid4()},
            })
            configGroup.parameters.nodes.addEntry(configVariant)
        }

        configGroup.parameters.nodes.addEntry(materialSwitch)
        return configGroup
    }
}
